import { useCallback, useEffect, useRef, useState, type ImgHTMLAttributes, type ReactNode } from 'react';

import { fetchAuthenticatedBlob } from '../../api/client';
import { useAuth } from '../../features/auth/AuthProvider';

type AuthenticatedImageProps = Omit<ImgHTMLAttributes<HTMLImageElement>, 'src'> & {
  readonly path?: string | null;
  readonly file?: File | null;
  readonly revision?: string | number | null;
  readonly fallback?: ReactNode;
  readonly errorFallback?: ReactNode;
};

type LoadedImage = {
  readonly source: File | string;
  readonly token: string | undefined;
  readonly revision: string | number | null | undefined;
  readonly url: string;
};

type FailedImage = Omit<LoadedImage, 'url'>;

/** Fetches protected image bytes with bearer auth and revokes its object URL on cleanup. */
export function AuthenticatedImage({
  path,
  file,
  revision,
  fallback = null,
  errorFallback,
  onError,
  ...imageProps
}: AuthenticatedImageProps) {
  const { tokens } = useAuth();
  const token = tokens?.access_token;
  const source = file ?? path ?? null;
  const [loadedImage, setLoadedImage] = useState<LoadedImage | null>(null);
  const [failedImage, setFailedImage] = useState<FailedImage | null>(null);
  const revokedObjectUrlsRef = useRef(new Set<string>());
  const revokeObjectUrl = useCallback((url: string) => {
    if (revokedObjectUrlsRef.current.has(url)) return;
    URL.revokeObjectURL(url);
    revokedObjectUrlsRef.current.add(url);
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    let objectUrl: string | null = null;
    let cancelled = false;
    setLoadedImage(null);
    setFailedImage(null);

    if (!source) return;

    if (source instanceof File) {
      objectUrl = URL.createObjectURL(source);
      revokedObjectUrlsRef.current.delete(objectUrl);
      setLoadedImage({ source, token, revision, url: objectUrl });
      return () => revokeObjectUrl(objectUrl!);
    }

    if (!token) return;

    void fetchAuthenticatedBlob(source, token, { signal: controller.signal }).then(
      (blob) => {
        if (cancelled) return;
        objectUrl = URL.createObjectURL(blob);
        revokedObjectUrlsRef.current.delete(objectUrl);
        setLoadedImage({ source, token, revision, url: objectUrl });
      },
      () => {
        if (!cancelled) setFailedImage({ source, token, revision });
      },
    );

    return () => {
      cancelled = true;
      controller.abort();
      if (objectUrl) revokeObjectUrl(objectUrl);
    };
  }, [revision, revokeObjectUrl, source, token]);

  const matchesCurrentSource = (image: FailedImage | LoadedImage | null) =>
    image?.source === source && image.token === token && image.revision === revision;

  if (!source) return <>{fallback}</>;
  if ((!file && !token) || matchesCurrentSource(failedImage)) {
    return <>{errorFallback ?? fallback}</>;
  }
  if (!matchesCurrentSource(loadedImage)) return null;
  return (
    <img
      {...imageProps}
      src={loadedImage!.url}
      onError={(event) => {
        revokeObjectUrl(loadedImage!.url);
        setLoadedImage(null);
        setFailedImage({ source, token, revision });
        onError?.(event);
      }}
    />
  );
}
